import { ErrorBoundary } from 'components/Errors/ErrorBoundary';
import LoadingScreen from 'components/LoadingScreen';
import PageLayout from 'components/PageLayout';
import { FilterConfig } from 'components/SearchAndFilters/FilterTypes';
import ActiveFiltersBar from 'components/SearchAndFilters/SelectedFiltersBar';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { Suspense } from 'react';
import { ToastContainer } from 'react-toastify';
import { CardTitle, Col, Row } from 'reactstrap';

import BPAScanDataGrid from './BPAScanDataGrid';
import Filters from './Filters';
import { useFilterConfig } from './Filters.config';
import { applyFiltersAtom, bpaScansDataAtom, filterValuesAtom } from './state';

const SelectedFilters: React.FC<{
  filtersConfig: FilterConfig;
  configInitKey: number;
}> = ({ filtersConfig, configInitKey }) => {
  const applyFilters = useSetAtom(applyFiltersAtom);
  // Combine filterValues + configInitKey so ActiveFiltersBar remounts:
  //  - on every user filter change (filterValues changes), AND
  //  - on page return, once applyInitialFilterValues has run (configInitKey increments).
  // This bypasses the stale useMemo([filtersConfig]) inside ActiveFiltersBar.
  const filterValues = useAtomValue(filterValuesAtom);

  return (
    <Suspense fallback={<></>}>
      <ActiveFiltersBar
        key={`${JSON.stringify(filterValues)}-${configInitKey}`}
        filtersConfig={filtersConfig}
        onFiltersReset={() => {
          // Called after either a single badge removal or "reset all".
          // In both cases the class instance has already been mutated, so
          // reading getActiveFilterValues() here returns only the remaining
          // active filters — empty when all were reset, partial otherwise.
          void applyFilters(filtersConfig.getActiveFilterValues());
        }}
      />
    </Suspense>
  );
};

const ListingCount: React.FC = () => {
  const listingData = useAtomValue(bpaScansDataAtom);
  return <>{listingData?.loading ? '...' : (listingData?.count ?? 0)}</>;
};

/**
 * BPA Scans listing page.
 */
const BPAScans: React.FC = () => {
  const { config: filtersConfig, initKey } = useFilterConfig();
  return (
    <PageLayout
      id="bpa-scans-listing"
      heading={
        <Row>
          <Col md="auto" sm="12">
            <CardTitle>
              <h4>
                BPA Scans
                <label id="counts" className="label btn">
                  <Suspense fallback={<>...</>}>
                    <ListingCount />
                  </Suspense>
                </label>
              </h4>
            </CardTitle>
          </Col>
        </Row>
      }
      subheading={<span>List of BPA Scans.</span>}
      description={<p>View and manage all BPA scan activities.</p>}
      filteringOptions={<Filters filtersConfig={filtersConfig} />}
    >
      <Suspense fallback={<LoadingScreen />}>
        <div className="container-fluid p-0">
          <div className="row mb-1">
            <div className="col p-1">
              <SelectedFilters
                filtersConfig={filtersConfig}
                configInitKey={initKey}
              />
            </div>
          </div>
          <BPAScanDataGrid />
        </div>
      </Suspense>
    </PageLayout>
  );
};

const BPAScanListing: React.FC = () => {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <div className="content">
        <ErrorBoundary>
          <BPAScans />
        </ErrorBoundary>
      </div>
      <ToastContainer />
    </Suspense>
  );
};

export default BPAScanListing;
